<!--
  - Copyright 2020 AlexaCRM
  -
  - Permission is hereby granted, free of charge, to any person obtaining a copy of this software and
  - associated documentation files (the "Software"), to deal in the Software without restriction, including
  - without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  - copies of the Software, and to permit persons to whom the Software is furnished to do so,
  - subject to the following conditions:
  -
  - The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
  -
  - THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
  - INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
  - PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS
  - BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
  - TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE
  - OR OTHER DEALINGS IN THE SOFTWARE.
  -->

<template>
  <div>
    <!-- The container is nested in case reCAPTCHA decides to replace the DOM node. -->
    <div :id="containerId"></div>
  </div>
</template>

<script>
  import {
    TYPE_V2_CHECKBOX,
    TYPE_V2_INVISIBLE,
    TYPE_V3,
    BADGE_BOTTOMRIGHT,
    THEME_LIGHT,
    SIZE_NORMAL
  } from '@/store/settings/recaptcha'
  import uniqueId from 'lodash/uniqueId'

  const RECAPTCHA_URL = 'https://www.google.com/recaptcha/api.js?onload=icdsRecaptchaOnloadCallback'

  const E_SOLVED = 'solved', E_SOLVED_INVISIBLE = 'solved-invisible', E_FAILED = 'failed', E_LOADED = 'loaded'

  /**
   * Contains the global reCAPTCHA object once it is loaded and initialized.
   */
  let grecaptcha

  /**
   * Promise that is resolved upon reCAPTCHA initialization.
   */
  let pOnload, pResolve

  /**
   * Adds a reCAPTCHA script onto the page. Only does that once.
   *
   * Specify site key to load reCAPTCHA v3.
   */
  function requireRecaptcha(id, siteKey) {
    const el = document.createElement('script')
    el.type = 'text/javascript'
    el.async = true
    el.defer = true
    el.id = id

    el.src = RECAPTCHA_URL + (siteKey ? `&render=${siteKey}` : '&render=explicit')

    document.body.appendChild(el);

    // The promise is resolved in icdsRecaptchaOnloadCallback which is called by reCAPTCHA.
    pOnload = new Promise(resolve => {
      pResolve = resolve
    });

    return pOnload;
  }

  /**
   * reCAPTCHA onload callback.
   */
  window.icdsRecaptchaOnloadCallback = () => {
    grecaptcha = window.grecaptcha
    pResolve() // pOnload is resolved here.
  };

  export default {
    name: 'VueRecaptcha',

    mounted() {
      this.id = uniqueId('icds-recaptcha-')
      const args = [this.id]

      if (this.type === TYPE_V3) {
        args.push(this.siteKey);
      }

      requireRecaptcha(...args).then(() => {
        this.$emit(E_LOADED);
        this.render();
      })
    },

    destroyed() {
      const scriptEl = document.getElementById(this.id);
      if (scriptEl) {
        const rcScriptEl = scriptEl.parentNode.querySelector('script[src*="gstatic"]')
        if (rcScriptEl) {
          rcScriptEl.remove()
        }

        scriptEl.remove()
      }

      if (this.recaptchaId) {
        grecaptcha.reset(this.recaptchaId)
      }
    },

    props: ['type', 'siteKey', 'theme', 'size', 'badge'],

    data() {
      return {
        id: '',
        recaptchaId: null
      }
    },

    computed: {
      containerId() {
        return this.id + '-container'
      },

      recaptchaSettings() {
        if (this.type === TYPE_V3) {
          return {}
        }

        if (this.type === TYPE_V2_INVISIBLE) {
          return {
            sitekey: this.siteKey,
            callback: token => this.$emit(E_SOLVED_INVISIBLE, token),
            'expired-callback': () => this.$emit(E_FAILED),
            'error-callback': () => this.$emit(E_FAILED),
            badge: this.badge || BADGE_BOTTOMRIGHT,
            size: 'invisible',
          }
        }

        if (this.type === TYPE_V2_CHECKBOX) {
          return {
            sitekey: this.siteKey,
            callback: token => this.$emit(E_SOLVED, token),
            'expired-callback': () => this.$emit(E_FAILED),
            'error-callback': () => this.$emit(E_FAILED),
            theme: this.theme || THEME_LIGHT,
            size: this.size || SIZE_NORMAL,
          }
        }

        return {}
      }
    },

    methods: {
      render() {
        if (this.type === TYPE_V3) {
          return
        }

        this.recaptchaId = grecaptcha.render(this.containerId, this.recaptchaSettings)
      },

      solve() {
        if (this.type === TYPE_V3) {
          try {
            grecaptcha.execute(this.siteKey).then(token => this.$emit(E_SOLVED_INVISIBLE, token))
          } catch (e) {
            this.$emit(E_FAILED, 'Failed to load or execute reCAPTCHA. Check your keys and try again.')
          }

          return
        }

        if (this.type === TYPE_V2_INVISIBLE) {
          grecaptcha.execute(this.recaptchaId)
        }

        // V2 Checkbox is solved via user interaction.
      },

      reload() {
        if (this.type === TYPE_V3) {
          this.render()
          return
        }

        if (this.recaptchaId !== null) {
          grecaptcha.reset(this.recaptchaId)
        }
      }
    }
  }
</script>

<style lang="scss" scoped>

</style>
